import { ContactTypeEditPage } from '@agentic-flow-studio/shared/components/contacts/ContactTypeEditPage';
import { ContactTypesPage } from '@agentic-flow-studio/shared/components/contacts/ContactTypesPage';
import {
  AppShellProvider,
  ProtectedRoute,
  WorkspaceLayout,
  WorkspaceRedirector,
} from '@agentic-flow-studio/shared/components/layout/AppShell';
import {
  AiProviderKeysSettingsSection,
  AppearanceSettingsSection,
  BillingSettingsSection,
  FeaturesSettingsSection,
  MembersSettingsSection,
  NotificationSettingsSection,
} from '@agentic-flow-studio/shared/components/settings/SettingsLayout';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { AppSidebar } from './components/AppSidebar';
import { JobPlanSettingsSection } from './components/settings/JobPlanSettingsSection';
import { buildNavItems } from './lib/nav-items';
import CalendarPage from './pages/CalendarPage';
import ContactsPage from './pages/ContactsPage';
import JobDetailPage from './pages/JobDetailPage';
import JobFormPage from './pages/JobFormPage';
import JobIngestPage from './pages/JobIngestPage';
import JobsPage from './pages/JobsPage';
import LocationFormPage from './pages/LocationFormPage';
import LocationsPage from './pages/LocationsPage';
import Login from './pages/Login';
import Notifications from './pages/Notifications';
import OAuthComplete from './pages/OAuthComplete';
import OperatorFormPage from './pages/OperatorFormPage';
import OperatorsPage from './pages/OperatorsPage';
import Profile from './pages/Profile';
import Register from './pages/Register';
import SchedulePage from './pages/SchedulePage';
import SettingsPage from './pages/SettingsPage';
import SkillFormPage from './pages/SkillFormPage';
import SkillsPage from './pages/SkillsPage';

export default function App() {
  return (
    <AppShellProvider>
      <Router>
        <Routes>
          <Route path="/" element={<WorkspaceRedirector defaultPath="schedule" />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/oauth-complete" element={<OAuthComplete />} />

          {/* Catch-all redirects for non-prefixed routes */}
          <Route path="/dashboard" element={<WorkspaceRedirector defaultPath="schedule" />} />
          <Route path="/schedule" element={<WorkspaceRedirector defaultPath="schedule" />} />
          <Route path="/calendar" element={<WorkspaceRedirector defaultPath="calendar" />} />
          <Route path="/jobs" element={<WorkspaceRedirector defaultPath="jobs" />} />
          <Route path="/contacts" element={<WorkspaceRedirector defaultPath="contacts" />} />
          <Route path="/operators" element={<WorkspaceRedirector defaultPath="operators" />} />
          <Route path="/skills" element={<WorkspaceRedirector defaultPath="skills" />} />
          <Route path="/locations" element={<WorkspaceRedirector defaultPath="locations" />} />
          <Route path="/settings" element={<WorkspaceRedirector defaultPath="settings" />} />
          <Route path="/profile" element={<WorkspaceRedirector defaultPath="profile" />} />
          <Route
            path="/notifications"
            element={<WorkspaceRedirector defaultPath="notifications" />}
          />

          {/* Authed Workspace Layout */}
          <Route
            path="/org/:orgId/workspace/:workspaceId"
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  SidebarComponent={AppSidebar}
                  buildDestinations={buildNavItems}
                  productKey="job_plan"
                />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="schedule" replace />} />
            <Route path="dashboard" element={<Navigate to="schedule" replace />} />
            <Route path="schedule" element={<SchedulePage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="jobs" element={<JobsPage />} />
            <Route path="jobs/new" element={<JobFormPage mode="create" />} />
            <Route path="jobs/:jobId" element={<JobDetailPage />} />
            <Route path="jobs/:jobId/edit" element={<JobFormPage mode="edit" />} />
            <Route path="contacts" element={<ContactsPage />} />
            <Route path="contacts/:contactId" element={<ContactsPage />} />
            <Route path="contacts/edit/:contactId" element={<ContactsPage />} />
            <Route path="operators" element={<OperatorsPage />} />
            <Route path="operators/new" element={<OperatorFormPage mode="create" />} />
            <Route path="operators/:operatorId/edit" element={<OperatorFormPage mode="edit" />} />
            <Route path="skills" element={<SkillsPage />} />
            <Route path="skills/new" element={<SkillFormPage mode="create" />} />
            <Route path="skills/:skillId/edit" element={<SkillFormPage mode="edit" />} />
            <Route path="locations" element={<LocationsPage />} />
            <Route path="locations/new" element={<LocationFormPage mode="create" />} />
            <Route path="locations/:locationId/edit" element={<LocationFormPage mode="edit" />} />
            <Route path="profile" element={<Profile />} />
            <Route path="notifications" element={<Notifications />} />
          </Route>

          {/* Job Plan Ingest Full-Page Takeover Route */}
          <Route
            path="/org/:orgId/workspace/:workspaceId/schedule/ingest"
            element={
              <ProtectedRoute>
                <JobIngestPage />
              </ProtectedRoute>
            }
          />

          {/* Settings Full-Page Takeover Route (SettingsSidebar ONLY) */}
          <Route
            path="/org/:orgId/workspace/:workspaceId/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="job-plan" replace />} />
            <Route path="job-plan" element={<JobPlanSettingsSection />} />
            <Route path="accounts" element={<Navigate to="../job-plan" replace />} />
            <Route path="contact-types" element={<ContactTypesPage />} />
            <Route path="contact-types/:id/edit" element={<ContactTypeEditPage />} />
            <Route path="appearance" element={<AppearanceSettingsSection />} />
            <Route path="notifications" element={<NotificationSettingsSection />} />
            <Route path="members" element={<MembersSettingsSection />} />
            <Route path="features" element={<FeaturesSettingsSection />} />
            <Route path="ai-provider" element={<AiProviderKeysSettingsSection />} />
            <Route path="billing" element={<BillingSettingsSection />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AppShellProvider>
  );
}
